import { toValue } from 'vue'
import utils from '@/utils/utils'

function date(input, format='MM/DD/YY') {
  var value = toValue(input)
  if (!value) return null
  return dayjs(value).format(format)
}

function dateLong(input) {
  return date(input, 'ddd MMM DD, YYYY')
}

function time(input, format='h:mma') {
  var value = toValue(input)  
  if (!value) return null
  return dayjs(value).format(format).replace(/am/gi, "a").replace(/pm/gi, "p")
}

function dateTime(input, format='MMM DD, YYYY • hh:mm a') {
  var value = toValue(input)
  if (!value) return null
  return dayjs(value).format(format) //.replace(/am/gi, "a").replace(/pm/gi, "p")  
}

function toDate(input, time = false) {
  var value = toValue(input)
  if (!value) return null
  return dayjs(value).toDate()
}

function currency(input, isRounded = false) {
  var value = toValue(input)
  if (typeof value !== "number") return value

  if (isRounded) {
    return currencyFormatterRounded.format(Math.round(value));
  } else {
    return currencyFormatterWithPennies.format(value);  
  }
}

function number(input, numDigits = 2) {
  var value = toValue(input)  
  if (typeof value !== "number") return value

  let options = {minimumFractionDigits: 1, maximumFractionDigits: numDigits}
  if (numDigits == 0) {
    options = {minimumFractionDigits: 0, maximumFractionDigits: 0} 
  }
  return new Intl.NumberFormat('en-US', options).format(value)
}

function percent(input, numDigits = 2) {
  var value = toValue(input)  
  if (typeof value !== "number") return value

  let options
  if (numDigits == 0) {
    options = {minimumFractionDigits: 0, maximumFractionDigits: 0} 
  } else {
    options = {minimumFractionDigits: 1, maximumFractionDigits: numDigits}
  }
  options = {...options, ...{style: 'percent'}}
  return new Intl.NumberFormat('en-US', options).format(value)
}

function dateRange(sDate, eDate, formatString='MMM DD, YYYY') {
  const startDate = dayjs(sDate).format(formatString)
  const endDate = dayjs(eDate).format(formatString)
  const dateStr = `${startDate} - ${endDate}`
  return dateStr
}

function dateRangeWithTime(sDate, eDate) {
  const startDate = dayjs(sDate)
  const endDate   = dayjs(eDate)
  const isSameDay = startDate.isSame(endDate, 'day')
  const dateFormat = 'MMM DD, YYYY'
  const timeFormat = 'h:mma'

  let dateStr

  if (isSameDay) {
    const minutes = endDate.diff(startDate, 'minute')
    const showTime = minutes < 1410 // 1410 min == 11.5 hours, we assume it's all day and don't show time
    dateStr = `${startDate.format(dateFormat)}`
    if (showTime) {
      dateStr = `${dateStr},  ${startDate.format(timeFormat)} - ${endDate.format(timeFormat)}`
    }
  } else {
    dateStr = `${startDate.format(dateFormat)} - ${endDate.format(dateFormat)}`
  }
  return dateStr
}

function dateToNow(input) {
  return dayjs().to( dayjs(input) ) // ex: 3 days ago
}

function humanize(str) {
  return str
    .replace(/^[\s_]+|[\s_]+$/g, '')
    .replace(/[_\s]+/g, ' ')
    .replace(/^[a-z]/, function(m) { return m.toUpperCase(); });
}

function humanizeDuration(startAt, endAt) {
  const date1 = dayjs(startAt)
  const date2 = dayjs(endAt)
  const duration = dayjs.duration(date2.diff(date1))

  const totalMinutes = duration.asMinutes()
  const totalDays = Math.floor(duration.asDays())

  if (totalDays >= 1) {
    const remainingMinutes = totalMinutes - totalDays * 24 * 60
    const fractionalHours = Math.round((remainingMinutes / 60) * 4) / 4
    const days = fractionalHours >= 24 ? totalDays + 1 : totalDays
    const arr = [days === 1 ? '1 day' : `${days} days`]
    if (fractionalHours > 0 && fractionalHours < 24) {
      arr.push(fractionalHours === 1 ? '1 hour' : `${fractionalHours} hours`)
    }
    return arr.join(', ')
  }

  const totalHours = duration.asHours()
  if (totalHours >= 1) {
    const fractionalHours = Math.round(totalHours * 4) / 4
    if (fractionalHours >= 24) {
      const days = Math.round(fractionalHours / 24)
      return days === 1 ? '1 day' : `${days} days`
    }
    return fractionalHours === 1 ? '1 hour' : `${fractionalHours} hours`
  }

  const minutes = duration.minutes()
  if (minutes > 0) return minutes === 1 ? '1 minute' : `${minutes} minutes`

  const seconds = duration.seconds()
  return seconds === 1 ? '1 second' : `${seconds} seconds`
}

// Pluralize Words helper - Inspired by this code
// https://www.30secondsofcode.org/js/s/pluralize-string/
const PLURAL_DICTIONARY = {
  person: 'people',
  radius: 'radii',
}

function pluralize(word, num, showNum=true) {
  const isPlural = Math.abs(num) !== 1
  let str = ''

  if (isPlural) {
    const simplePlural = `${word}s`
    const isInDictionary = Object.keys(PLURAL_DICTIONARY).includes(word.toLowerCase())

    if (isInDictionary) {
      str = PLURAL_DICTIONARY[word.toLowerCase()]    
    } else {
      str = simplePlural
    }    
  } else {
    // if not plural we don't change the word
    str = word
  }

  str = showNum ? `${num} ${str}` : str
  return str
}

// Private helpers

const currencyFormatterRounded = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0, // don't show pennies
})

const currencyFormatterWithPennies = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2, // don't show pennies
})

// const dateFormats = {
//   shortDate: 'MM/DD/YY',
//   shortDateTime: 'MM/DD/YY HH:mm',
// }

function fileSize(bytes) {
  if (!bytes && bytes !== 0) return '—'
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`
  return `${Math.round(bytes / 1024)} KB`
}

const format = {
  currency,
  capitalize: utils.capitalize,
  date,
  dateLong,
  dateTime,
  fileSize,
  humanize,
  humanizeDuration,
  pluralize,
  startCase: utils.startCase,
  toUpper: utils.toUpper,
  time,
  dateRange,
  dateToNow,
  dateRangeWithTime,
  number,
  percent,
  toDate,
}

export default format